import type {
  AysCodeCodingSystem,
  AysCodeHierarchyElementType,
} from './code-type.model';
import type { Cloneable } from '@bindoc-projects/util-domain-lib';
import { normalizeAvailableYears } from './code-years';

export interface AysCodeResponse {
  name: string;
  hierarchyElementType: AysCodeHierarchyElementType;
  codingSystem: AysCodeCodingSystem;
  description?: string;
  year?: number;
  /**
   * The years this code actually has data for. `null` when the backend cannot
   * tell — consumers then fall back to the full InEK year range.
   */
  availableYears?: number[] | null;
}

export class AysCode implements Cloneable<AysCode> {
  public static validateCodeName(
    codeName: string,
    codingSystem: AysCodeCodingSystem,
  ): boolean {
    let result;

    switch (codingSystem) {
      case 'OPS':
        result = !!codeName.match(
          /^([135689]|[0-9]-[0-9]{2}\.\.\.[0-9]-[0-9]{2}|[0-9]-[0-9][0-9]([a-z0-9](\.[a-z0-9]{1,2})?)?|a-aaa)$/,
        );
        break;
      case 'ICD-10-GM':
      case 'ICD-10-AT':
        result = !!codeName.match(
          /^(X?(XI{0,2}|IX|IV|V?I{0,3})|[A-Z][0-9][0-9]-[A-Z][0-9][0-9]|[A-Z][0-9][0-9]((\.[0-9]{1,2}[!#*+]?)|[!#*+]?)?|AAA)$/,
        );
        break;
      case 'G-DRG':
        result = !!codeName.match(
          /^(-1|0[0-9]|1[0-7]|18[AB]|19|20|21[AB]|2[2-4]|[A-Z0-9][0-9][0-9][A-Z]?|AAAA)$/,
        );
        break;
      case 'PROCEDURE-AT':
        result = !!codeName.match(
          /^(0[1-9]|1[0-9]|2[0-6]|[0-9]{2}\.[0-9]{2}|[A-Z]{2}[0-9]{3})$/,
        );
        break;
      default:
        result = true;
    }

    return result;
  }

  public static fromObject(obj: AysCodeResponse): AysCode {
    return new AysCode(
      obj.name,
      obj.description,
      obj.codingSystem,
      obj.hierarchyElementType,
      obj.year,
      normalizeAvailableYears(obj.availableYears),
    );
  }

  constructor(
    public name: string,
    public description: string | undefined,
    public codingSystem: AysCodeCodingSystem,
    public hierarchyElementType: AysCodeHierarchyElementType,
    public year?: number,
    public availableYears?: number[],
  ) {}

  clone(): AysCode {
    return new AysCode(
      this.name,
      this.description,
      this.codingSystem,
      this.hierarchyElementType,
      this.year,
      this.availableYears ? [...this.availableYears] : undefined,
    );
  }

  public numberOfDigits(): 1 | 3 | 4 | 5 | 6 | undefined {
    switch (this.hierarchyElementType) {
      case 'chapter': {
        return 1;
      }
      case 'block':
        return undefined;
      case 'specific': {
        switch (this.codingSystem) {
          case 'G-DRG': {
            return 3;
          }
          case 'ICD-10-GM':
          case 'ICD-10-AT':
          case 'ICD-10-UK': {
            return this._cleanName(this.name).length as 3 | 4 | 5;
          }
          case 'OPS': {
            return this._cleanName(this.name).length as 3 | 4 | 5 | 6;
          }
          default:
            return undefined;
        }
      }
    }
  }

  public toJSON(): object {
    return {
      name: this.name,
      codingSystem: this.codingSystem,
      hierarchyElementType: this.hierarchyElementType,
      year: this.year,
    };
  }

  private _cleanName(name: string): string {
    const regex = new RegExp('[-+.!*#]');
    if (name.match(regex)) {
      return this._cleanName(name.replace(regex, ''));
    } else {
      return name;
    }
  }
}

export class AysCodeCount {
  public static fromObjArr(
    objArr: { code?: AysCodeResponse; count: number }[],
  ): AysCodeCount[] {
    return objArr.map(AysCodeCount.fromObj);
  }

  public static fromObj(obj: {
    code?: AysCodeResponse;
    count: number;
  }): AysCodeCount {
    const code = obj.code ? AysCode.fromObject(obj.code) : null;

    return new AysCodeCount(code, obj.count);
  }

  constructor(
    public code: AysCode,
    public count: number,
  ) {}
}
